/**
 * Account and cloud storage actions. Cloud projects use the same project file as local storage and
 * downloads; the server keeps the original image next to it.
 */
import { newId } from '../engine/model/design';
import { t } from '../i18n/i18n';
import { type ImageData8, type LoadedProject, buildProjectFile, getProjectRecord } from '../project/db';
import { type ProjectFile, base64ToBytes, bytesToBase64, migrateProject } from '../project/format';
import { type Store, store as defaultStore } from '../state/store';
import { applyLoaded, imageData8 } from '../ui/actions';
import { renderThumbnail } from '../ui/canvas/render';
import { type AccountUser, api, fetchMe } from './api';

/** Asks the server whether accounts are available and who is logged in. */
export async function loadAccount(s: Store = defaultStore): Promise<void> {
  const me = await fetchMe();
  s.set({ account: { enabled: !!me?.accounts, user: me?.user ?? null } });
}

export function setUser(user: AccountUser | null, s: Store = defaultStore): void {
  s.set((st) => ({ account: { ...st.account, user } }));
}

export async function logout(s: Store = defaultStore): Promise<void> {
  await api('POST', '/api/auth/logout');
  setUser(null, s);
  s.toast('info', t('You are logged out. Projects in this browser are still here.'));
}

// ---------------------------------------------------------------- cloud projects

export interface CloudProjectSummary {
  id: string;
  name: string;
  thumbnail: string | null;
  objectCount: number;
  folder: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listCloudProjects(): Promise<CloudProjectSummary[]> {
  return (await api<{ projects: CloudProjectSummary[] }>('GET', '/api/projects')).projects;
}

export async function deleteCloudProject(id: string, s: Store = defaultStore): Promise<void> {
  await api('DELETE', `/api/projects/${encodeURIComponent(id)}`);
  if (s.getState().project.id === id) s.set((st) => ({ project: { ...st.project, cloud: false, dirty: true } }));
}

/** Renames a cloud project and/or moves it to a folder. */
export async function patchCloudProject(id: string, patch: { name?: string; folder?: string | null }): Promise<void> {
  await api('PATCH', `/api/projects/${encodeURIComponent(id)}`, patch);
}

/** Uploads a project stored in this browser (without opening it). */
export async function uploadLocalProject(id: string): Promise<void> {
  const rec = await getProjectRecord(id);
  if (!rec) throw new Error(t('Project not found.'));
  await api('PUT', `/api/projects/${encodeURIComponent(id)}`, { file: { ...rec.file, image: null }, thumbnail: rec.thumbnail, image: imageToJson(rec.image) });
}

/** The image last uploaded per project: saving again without a new image skips the upload. */
const uploaded = new Map<string, unknown>();

/** Uploads the current project (logged-in users save to the cloud every time). */
export async function saveToCloud(s: Store = defaultStore, toast = true): Promise<void> {
  const st = s.getState();
  const designAtSave = s.design;
  const id = st.project.id ?? newId('p');
  const name = st.project.name.trim() || t('Untitled');
  const file = buildProjectFile(id, name, s.design, st.project.createdAt ?? undefined, st.project.folder);
  const result = st.stitch.result;
  const thumbnail = result ? renderThumbnail(result.sequence, 240, 180) : null;
  const decoded = st.image?.decoded ?? null;
  const body: Record<string, unknown> = { file, thumbnail };
  if (uploaded.get(id) !== decoded) body.image = imageToJson(imageData8(s));
  const r = await api<{ updatedAt: string }>('PUT', `/api/projects/${encodeURIComponent(id)}`, body);
  uploaded.set(id, decoded);
  s.set((cur) => ({ project: { ...cur.project, id, name, createdAt: file.createdAt, cloud: true, dirty: cur.history.present !== designAtSave, lastSaved: r.updatedAt } }));
  if (toast) s.toast('success', t('“{name}” is saved in the cloud.', { name }));
}

/** A cloud project as stored, validated like any project file. */
export async function loadCloudProject(id: string): Promise<LoadedProject> {
  const r = await api<{ file: unknown; image: (Omit<ImageData8, 'bytes'> & { base64: string }) | null }>('GET', `/api/projects/${encodeURIComponent(id)}`);
  const file: ProjectFile = migrateProject(r.file);
  const image: ImageData8 | null = r.image ? { ...r.image, bytes: base64ToBytes(r.image.base64).buffer as ArrayBuffer } : null;
  return { file, image };
}

/** Copies a cloud project under a new id and name (in the cloud). */
export async function duplicateCloudProject(id: string): Promise<void> {
  const [{ file, image }, list] = await Promise.all([loadCloudProject(id), listCloudProjects()]);
  const copyId = newId('p');
  const name = t('{name} (copy)', { name: file.name });
  const now = new Date().toISOString();
  const copy = { ...file, id: copyId, name, createdAt: now, updatedAt: now, design: { ...file.design, name }, image: null };
  await api('PUT', `/api/projects/${encodeURIComponent(copyId)}`, { file: copy, thumbnail: list.find((p) => p.id === id)?.thumbnail ?? null, image: imageToJson(image) });
}

export async function openCloudProject(id: string, s: Store = defaultStore): Promise<void> {
  const { file, image } = await loadCloudProject(id);
  await applyLoaded(file, image, s, true);
  uploaded.set(file.id, s.getState().image?.decoded ?? null);
  s.set((st) => ({ project: { ...st.project, cloud: true } }));
}

function imageToJson(img: ImageData8 | null) {
  return img ? { mime: img.mime, name: img.name, width: img.width, height: img.height, base64: bytesToBase64(new Uint8Array(img.bytes)) } : null;
}
